import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { renderHTML } from "../hooks/htmlToText";
import { useSectionObserver } from "../hooks/useSectionObserver";

export const TableOfContentsPrivacyPolicy = () => {
  const { t } = useTranslation();
  useSectionObserver([
    "info-collect",
    "info-process",
    "legal-bases",
    "info-share",
    "cookies",
    "info-retention",
    "info-security",
    "minors",
    "privacy-rights",
    "dnt",
    "us-rights",
    "updates",
    "contact",
    "review-data",
  ]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  return (
    <div className="gap-8 flex flex-col">
      <h1 className="text-[20px] md:text-[24px] font-600 uppercase">
        {String(t("policyPage.table.title"))}
      </h1>
      <ol className="uppercase font-600 list-auto flex flex-col gap-2 ml-[30px]">
        <li>
          <a href={`#info-collect`} aria-label="info-collect">
            {String(t("policyPage.table.t1"))}
          </a>
        </li>
        <li>
          <a href={`#info-process`} aria-label="info-process">
            {String(t("policyPage.table.t2"))}
          </a>
        </li>
        <li>
          <a href={`#legal-bases`} aria-label="legal-bases">
            {String(t("policyPage.table.t3"))}
          </a>
        </li>
        <li>
          <a href={`#info-share`} aria-label="info-share">
            {String(t("policyPage.table.t4"))}
          </a>
        </li>
        <li>
          <a href={`#cookies`} aria-label="cookies">
            {String(t("policyPage.table.t5"))}
          </a>
        </li>
        <li>
          <a href={"#info-retention"} aria-label="info-retention">
            {String(t("policyPage.table.t6"))}
          </a>
        </li>
        <li>
          <a href={"#info-security"} aria-label="info-security">
            {String(t("policyPage.table.t7"))}
          </a>
        </li>
        <li>
          <a href={"#minors"} aria-label="minors">
            {String(t("policyPage.table.t8"))}
          </a>
        </li>
        <li>
          <a href={"#privacy-rights"} aria-label="privacy-rights">
            {String(t("policyPage.table.t9"))}
          </a>
        </li>
        <li>
          <a href={"#dnt"} aria-label="dnt">
            {String(t("policyPage.table.t10"))}
          </a>
        </li>
        <li>
          <a href={"#us-rights"} aria-label="us-rights">
            {String(t("policyPage.table.t11"))}
          </a>
        </li>
        <li>
          <a href={"#updates"} aria-label="updates">
            {String(t("policyPage.table.t12"))}
          </a>
        </li>
        <li>
          <a href={"#contact"} aria-label="contact">
            {String(t("policyPage.table.t13"))}
          </a>
        </li>
        <li>
          <a href={"#review-data"} aria-label="review-data">
            {String(t("policyPage.table.t14"))}
          </a>
        </li>
      </ol>
      <div className="flex flex-col gap-5">
        <div
          id={"info-collect"}
          className="gap-5 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            1. {String(t("policyPage.table.t1"))}
          </h2>
          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <p className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.first.p1")))}
            </p>
            <p>{renderHTML(String(t("policyPage.tableContent.first.p2")))}</p>
            <p>{renderHTML(String(t("policyPage.tableContent.first.p3")))}</p>
          </div>

          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <p className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.first.p4")))}
            </p>
            <div>
              {renderHTML(String(t("policyPage.tableContent.first.p5")))}
              <ul className="list-disc ml-[30px]">
                <li>
                  {renderHTML(String(t("policyPage.tableContent.first.ul6.1")))}
                </li>
                <li>
                  {renderHTML(String(t("policyPage.tableContent.first.ul6.2")))}
                </li>
                <li>
                  {renderHTML(String(t("policyPage.tableContent.first.ul6.3")))}
                </li>
              </ul>
            </div>
            <div>
              {renderHTML(String(t("policyPage.tableContent.first.p7")))}
              <ul className="list-disc ml-[30px]">
                <li className="font-600">
                  {renderHTML(String(t("policyPage.tableContent.first.ul7.1")))}
                </li>
                <li className="font-600">
                  {renderHTML(String(t("policyPage.tableContent.first.ul7.2")))}
                </li>
                <li className="font-600">
                  {renderHTML(String(t("policyPage.tableContent.first.ul7.3")))}
                </li>
              </ul>
            </div>
          </div>
          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <p className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.first.p8")))}
            </p>
            <p>{renderHTML(String(t("policyPage.tableContent.first.p9")))}</p>
            <p>{renderHTML(String(t("policyPage.tableContent.first.p10")))}</p>
          </div>
          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <p className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.first.p11")))}
            </p>
            <p>{renderHTML(String(t("policyPage.tableContent.first.p12")))}</p>
            <p>{renderHTML(String(t("policyPage.tableContent.first.p13")))}</p>
            <div>
              {renderHTML(String(t("policyPage.tableContent.first.p14")))}
              <ul className="list-disc ml-[30px]">
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.first.ul14.1"))
                  )}
                </li>
                <li className="font-600">
                  {renderHTML(
                    String(t("policyPage.tableContent.first.ul14.2"))
                  )}
                </li>
                <li className="font-600">
                  {" "}
                  {renderHTML(
                    String(t("policyPage.tableContent.first.ul14.3"))
                  )}{" "}
                </li>
              </ul>
            </div>
            <div>
              {renderHTML(String(t("policyPage.tableContent.first.p15")))}
              <ul className="list-disc ml-[30px]">
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.first.ul15.1"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.first.ul15.2"))
                  )}
                </li>
              </ul>
              <p>
                {renderHTML(String(t("policyPage.tableContent.first.p16")))}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <h2 className="text-[20px] md:text-[24px] font-600">
              {renderHTML(
                String(t("policyPage.tableContent.first.collect.title"))
              )}
            </h2>
            <ul className="list-disc ml-[30px]">
              <li>
                <span className="font-600">
                  {renderHTML(
                    String(t("policyPage.tableContent.first.collect.p1"))
                  )}
                </span>
                <ul className="list-circle ml-[30px]">
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul1"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul2"))
                    )}
                    <ul className="list-square ml-[30px]">
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul2_1")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul2_2")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul2_3")
                          )
                        )}
                      </li>
                      <li>
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul2_4")
                          )
                        )}
                      </li>
                      <li>
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul2_5")
                          )
                        )}
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
              <li>
                <span className="font-600">
                  {renderHTML(
                    String(t("policyPage.tableContent.first.collect.p2"))
                  )}
                </span>
                <ul className="list-circle ml-[30px]">
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul3"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul4"))
                    )}
                    <ul className="list-square ml-[30px]">
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_1")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_2")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_3")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_4")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_5")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_6")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_7")
                          )
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul4_8")
                          )
                        )}
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>

              <li>
                <span className="font-600">
                  {renderHTML(
                    String(t("policyPage.tableContent.first.collect.p3"))
                  )}
                </span>
                <ul className="list-circle ml-[30px]">
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul5"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul6"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul7"))
                    )}
                  </li>
                  <li>
                    <span className="font-600">
                      {renderHTML(
                        String(t("policyPage.tableContent.first.collect.ul8"))
                      )}
                    </span>
                    <ul className="list-square ml-[30px]">
                      <li>
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul8_1")
                          )
                        )}
                      </li>
                      <li>
                        {renderHTML(
                          String(
                            t("policyPage.tableContent.first.collect.ul8_2")
                          )
                        )}
                      </li>
                    </ul>
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.first.collect.ul9"))
                    )}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
        <div
          id={"info-process"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            2. {String(t("policyPage.table.t2"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.second.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.second.p2")))}</p>
          <ul className="list-disc ml-[30px]">
            <li>
              <span className="font-600">
                {renderHTML(String(t("policyPage.tableContent.second.ul1")))}
              </span>
              <ul className="list-circle ml-[30px]">
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.second.ul1_1"))
                  )}
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div
          id={"legal-bases"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            3. {String(t("policyPage.table.t3"))}
          </h2>
          <div>
            {renderHTML(String(t("policyPage.tableContent.third.p1")))}

            <p> {renderHTML(String(t("policyPage.tableContent.third.ul1")))}</p>
            <ul className="list-disc ml-[30px] font-600">
              <li>
                {renderHTML(String(t("policyPage.tableContent.third.ul1_1")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.third.ul1_2")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.third.ul1_3")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.third.ul1_4")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.third.ul1_5")))}
              </li>
            </ul>
          </div>
          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <p className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.third.subtitle1")))}
            </p>
            <div>
              <p>
                {renderHTML(String(t("policyPage.tableContent.third.eu.p1")))}
              </p>
              <p>
                {renderHTML(String(t("policyPage.tableContent.third.eu.p2")))}
              </p>
              <ol className="list-auto ml-[30px]">
                <li>
                  <span className="font-600">
                    {renderHTML(
                      String(t("policyPage.tableContent.third.eu.ol1"))
                    )}
                  </span>
                  <ul className="ml-[30px] list-lower-alpha">
                    <li>
                      {renderHTML(
                        String(t("policyPage.tableContent.third.eu.ol1_1"))
                      )}
                    </li>
                    <li>
                      <span className="font-600">
                        {renderHTML(
                          String(t("policyPage.tableContent.third.eu.ol1_2"))
                        )}
                      </span>
                    </li>
                    <li>
                      {renderHTML(
                        String(t("policyPage.tableContent.third.eu.ol1_3"))
                      )}
                    </li>
                  </ul>
                </li>
                <li>
                  <span className="font-600">
                    {renderHTML(
                      String(t("policyPage.tableContent.third.eu.ol2"))
                    )}
                  </span>
                  <ul className="ml-[30px] list-lower-alpha">
                    <li>
                      {renderHTML(
                        String(t("policyPage.tableContent.third.eu.ol2_1"))
                      )}
                      <ul className="ml-[30px] list-lower-roman">
                        <li>
                          {renderHTML(
                            String(
                              t("policyPage.tableContent.third.eu.ol2_1_1")
                            )
                          )}
                        </li>
                        <li>
                          {renderHTML(
                            String(
                              t("policyPage.tableContent.third.eu.ol2_1_2")
                            )
                          )}
                        </li>
                        <li>
                          {renderHTML(
                            String(
                              t("policyPage.tableContent.third.eu.ol2_1_3")
                            )
                          )}
                        </li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li>
                  <span className="font-600">
                    {renderHTML(
                      String(t("policyPage.tableContent.third.eu.ol3"))
                    )}
                  </span>
                  <ul className="ml-[30px] list-lower-alpha">
                    <li>
                      {renderHTML(
                        String(t("policyPage.tableContent.third.eu.ol3_1"))
                      )}
                      <ul className="ml-[30px] list-lower-roman">
                        <li>
                          <span className="font-600">
                            {renderHTML(
                              String(
                                t("policyPage.tableContent.third.eu.ol3_1_1")
                              )
                            )}
                          </span>
                        </li>
                        <li>
                          <span className="font-600">
                            {renderHTML(
                              String(
                                t("policyPage.tableContent.third.eu.ol3_1_2")
                              )
                            )}
                          </span>
                        </li>
                      </ul>
                    </li>
                    <li>
                      {renderHTML(
                        String(t("policyPage.tableContent.third.eu.ol3_2"))
                      )}
                    </li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.third.subtitle2")))}
            </h2>
            <div>
              {renderHTML(String(t("policyPage.tableContent.third.p1")))}
              <ul className="ml-[30px] list-disc">
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.canada.ul1"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.canada.ul2"))
                  )}
                </li>
              </ul>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.third.canada.p2"))
                )}
              </p>
            </div>
          </div>
          <div className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <h2 className="font-600 text-18px md:text-[20px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.third.subtitle3")))}
            </h2>
            <div>
              {renderHTML(
                String(t("policyPage.tableContent.third.exceptional.p1"))
              )}
              <ul className="ml-[30px] list-disc">
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul1"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul2"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul3"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul4"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul5"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul6"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul7"))
                  )}
                  <ul className="ml-[30px] list-circle">
                    <li className="font-600">
                      {renderHTML(
                        String(
                          t("policyPage.tableContent.third.exceptional.ul7_1")
                        )
                      )}
                    </li>
                    <li className="font-600">
                      {renderHTML(
                        String(
                          t("policyPage.tableContent.third.exceptional.ul7_2")
                        )
                      )}
                    </li>
                  </ul>
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul8"))
                  )}
                  <ul className="ml-[30px] list-circle font-600">
                    <li>
                      {renderHTML(
                        String(
                          t("policyPage.tableContent.third.exceptional.ul8_1")
                        )
                      )}
                    </li>
                    <li>
                      {renderHTML(
                        String(
                          t("policyPage.tableContent.third.exceptional.ul8_2")
                        )
                      )}
                    </li>
                    <li>
                      {renderHTML(
                        String(
                          t("policyPage.tableContent.third.exceptional.ul8_3")
                        )
                      )}
                    </li>
                    <li>
                      {renderHTML(
                        String(
                          t("policyPage.tableContent.third.exceptional.ul8_4")
                        )
                      )}
                    </li>
                  </ul>
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul9"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul10"))
                  )}
                </li>
                <li>
                  {renderHTML(
                    String(t("policyPage.tableContent.third.exceptional.ul11"))
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          id={"info-share"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            4. {String(t("policyPage.table.t4"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.fourth.p1")))}</p>
          <div>
            {renderHTML(String(t("policyPage.tableContent.fourth.p2")))}
            <ul className="ml-[30px] list-disc">
              <li>
                <span className="font-600">
                  {renderHTML(String(t("policyPage.tableContent.fourth.p3")))}
                </span>
                <ul className="ml-[30px] list-circle">
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.fourth.ul1"))
                    )}
                    <ul className="ml-[30px] list-square">
                      <li>
                        {renderHTML(
                          String(t("policyPage.tableContent.fourth.ul1_1"))
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(t("policyPage.tableContent.fourth.ul1_2"))
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(t("policyPage.tableContent.fourth.ul1_3"))
                        )}
                      </li>
                      <li className="font-600">
                        {renderHTML(
                          String(t("policyPage.tableContent.fourth.ul1_4"))
                        )}
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
        <div
          id={"cookies"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            5. {String(t("policyPage.table.t5"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.fifth.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.fifth.p2")))}</p>
          <div>
            <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.fifth.p3")))}
              <ul className="ml-[30px] list-disc">
                <li>
                  {renderHTML(String(t("policyPage.tableContent.fifth.ul1")))}
                </li>
                <li>
                  {renderHTML(String(t("policyPage.tableContent.fifth.ul2")))}
                </li>
                <li>
                  {renderHTML(String(t("policyPage.tableContent.fifth.ul3")))}
                </li>
                <li>
                  {renderHTML(String(t("policyPage.tableContent.fifth.ul4")))}
                </li>
              </ul>
            </h2>
          </div>
          <div>
            <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(
                String(t("policyPage.tableContent.fifth.thirth.title"))
              )}
            </h2>
            <ul className="ml-[30px] list-disc">
              <li>
                {renderHTML(
                  String(t("policyPage.tableContent.fifth.thirth.p1"))
                )}
                <ul className="ml-[30px] list-circle font-600">
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.fifth.thirth.ul1"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.fifth.thirth.ul2"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.fifth.thirth.ul3"))
                    )}
                  </li>
                  <li>
                    {renderHTML(
                      String(t("policyPage.tableContent.fifth.thirth.ul4"))
                    )}
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <div>
            <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(String(t("policyPage.tableContent.fifth.opt.title")))}
            </h2>
            <p>
              {renderHTML(String(t("policyPage.tableContent.fifth.opt.p1")))}
            </p>
          </div>
          <div>
            <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
              {renderHTML(
                String(t("policyPage.tableContent.fifth.more.title"))
              )}
            </h2>
            <p>
              {renderHTML(String(t("policyPage.tableContent.fifth.more.p1")))}
            </p>
          </div>
          <div>
            <h2 className="font-600 text-18px md:text-[20px] my-[10px]">
              {renderHTML(
                String(t("policyPage.tableContent.fifth.google.title"))
              )}
            </h2>
            <p>
              {renderHTML(String(t("policyPage.tableContent.fifth.google.p1")))}
            </p>
            <div>
              {renderHTML(String(t("policyPage.tableContent.fifth.google.p2")))}

              <ul className="ml-[30px] list-disc">
                <li className="font-600">
                  {renderHTML(
                    String(t("policyPage.tableContent.fifth.google.ul1"))
                  )}
                </li>
              </ul>
            </div>
            <div>
              {renderHTML(String(t("policyPage.tableContent.fifth.google.p3")))}
              <ul className="ml-[30px] list-disc">
                <li className="font-600">
                  {renderHTML(
                    String(t("policyPage.tableContent.fifth.google.ul2"))
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div
          id={"info-retention"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            6. {String(t("policyPage.table.t6"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.sixth.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.sixth.p2")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.sixth.p3")))}</p>
        </div>
        <div
          id={"info-security"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            7. {String(t("policyPage.table.t7"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.seventh.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.seventh.p2")))}</p>
          <div>
            {renderHTML(String(t("policyPage.tableContent.seventh.p3")))}
            <p>{renderHTML(String(t("policyPage.tableContent.seventh.p4")))}</p>
            <p className="font-600">
              {renderHTML(String(t("policyPage.tableContent.seventh.p5")))}
            </p>
          </div>
        </div>
        <div
          id={"minors"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            8. {String(t("policyPage.table.t8"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.eighth.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.eighth.p2")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.eighth.p3")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.eighth.p4")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.eighth.p5")))}</p>
        </div>
        <div
          id={"privacy-rights"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            9. {String(t("policyPage.table.t9"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.ninth.p1")))}</p>
          <div>
            {renderHTML(String(t("policyPage.tableContent.ninth.p2")))}
            <ol className="list-auto ml-[30px]">
              <li>
                {renderHTML(String(t("policyPage.tableContent.ninth.ol1")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.ninth.ol2")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.ninth.ol3")))}
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.ninth.ol4")))}
              </li>
              <li>
                <span className="font-600">
                  {renderHTML(String(t("policyPage.tableContent.ninth.ol5")))}
                </span>
              </li>
              <li>
                {renderHTML(String(t("policyPage.tableContent.ninth.ol6")))}
              </li>
            </ol>
          </div>
          <p>{renderHTML(String(t("policyPage.tableContent.ninth.p3")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.ninth.p4")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.ninth.p5")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.ninth.p6")))}</p>

          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.ninth.withdraw.title"))
            )}
          </h2>
          <p>
            {renderHTML(String(t("policyPage.tableContent.ninth.withdraw.p1")))}
          </p>
          <p>
            {renderHTML(String(t("policyPage.tableContent.ninth.withdraw.p2")))}
          </p>
          <p>
            {renderHTML(String(t("policyPage.tableContent.ninth.withdraw.p3")))}
          </p>
          <ul className="list-disc ml-[30px]">
            <li>
              {renderHTML(
                String(t("policyPage.tableContent.ninth.withdraw.ul1"))
              )}
            </li>
            <li>
              {renderHTML(
                String(t("policyPage.tableContent.ninth.withdraw.ul2"))
              )}
            </li>
          </ul>
          <p>
            <span className="font-600">
              {renderHTML(
                String(t("policyPage.tableContent.ninth.optout.title"))
              )}{" "}
            </span>
            {renderHTML(String(t("policyPage.tableContent.ninth.optout.p1")))}{" "}
            {renderHTML(String(t("policyPage.tableContent.ninth.optout.p2")))}
          </p>
          <p>
            <span className="font-600">
              {renderHTML(
                String(t("policyPage.tableContent.ninth.cookies.title"))
              )}
            </span>{" "}
            {renderHTML(String(t("policyPage.tableContent.ninth.cookies.p1")))}{" "}
            {renderHTML(String(t("policyPage.tableContent.ninth.cookies.p2")))}
          </p>
          <p>{renderHTML(String(t("policyPage.tableContent.ninth.p7")))}</p>
        </div>
        <div
          id={"dnt"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            10. {String(t("policyPage.table.t10"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.tenth.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.tenth.p2")))}</p>
        </div>
        <div
          id={"us-rights"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            11. {String(t("policyPage.table.t11"))}
          </h2>
          <p>{renderHTML(String(t("policyPage.tableContent.eleventh.p1")))}</p>

          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.cat.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.cat.subtitle"))
            )}
          </p>
          <div className="gap-4 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]">
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat1.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat1.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat1.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat2.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat2.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat2.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat3.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat3.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat3.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat4.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat4.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat4.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat5.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat5.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat5.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat6.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat6.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat6.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat7.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat7.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat7.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat8.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat8.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat8.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat9.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat9.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat9.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat10.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat10.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat10.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat11.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat11.ex"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat11.col"))
                )}
              </p>
            </div>
            <div>
              <p className="font-600">
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat12.title"))
                )}
              </p>
              <p>
                {renderHTML(
                  String(t("policyPage.tableContent.eleventh.cat.cat12.col"))
                )}
              </p>
            </div>
          </div>
          <p>{renderHTML(String(t("policyPage.tableContent.eleventh.p2")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.eleventh.p3")))}</p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.source.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.source.p1"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.use_and_share.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.use_and_share.p1"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.sharing_info.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.sharing_info.p1"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.sharing_info.p2"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.sharing_info.p3"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.rights.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.rights.p1"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.rights.p2"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(
                t("policyPage.tableContent.eleventh.how_to_exercise.title")
              )
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.how_to_exercise.p1"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.how_to_exercise.p2"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.verification.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.verification.p1"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.verification.p2"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.verification.p3"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.appeals.title"))
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.appeals.p1"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.appeals.p2"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.appeals.p3"))
            )}
          </p>
          <h2 className="font-600 text-16px md:text-[18px] my-[10px]">
            {renderHTML(
              String(
                t("policyPage.tableContent.eleventh.shine_the_light.title")
              )
            )}
          </h2>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.shine_the_light.p1"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.shine_the_light.p2"))
            )}
          </p>
          <p>
            {renderHTML(
              String(t("policyPage.tableContent.eleventh.shine_the_light.p3"))
            )}
          </p>
        </div>
        <div
          id={"updates"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            12. {String(t("policyPage.table.t12"))}
          </h2>

          <p>{renderHTML(String(t("policyPage.tableContent.twelfth.p1")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.twelfth.p2")))}</p>
          <p>{renderHTML(String(t("policyPage.tableContent.twelfth.p3")))}</p>
          <ul className="list-disc ml-[30px]">
            <li>
              {renderHTML(String(t("policyPage.tableContent.twelfth.ul1")))}
            </li>
            <li>
              {renderHTML(String(t("policyPage.tableContent.twelfth.ul2")))}
            </li>
          </ul>
          <p>{renderHTML(String(t("policyPage.tableContent.twelfth.p4")))}</p>
        </div>

        <div
          id={"contact"}
          className="flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            13. {String(t("policyPage.table.t13"))}
          </h2>

          <p>
            {renderHTML(String(t("policyPage.tableContent.thirteenth.p1")))}
          </p>
          <p className="font-600">
            {renderHTML(String(t("policyPage.tableContent.thirteenth.p2")))}
          </p>
          <p className="font-600">
            {renderHTML(String(t("policyPage.tableContent.thirteenth.p3")))}
          </p>
          {renderHTML(String(t("policyPage.tableContent.thirteenth.p4")))}
          <p>
            {renderHTML(String(t("policyPage.tableContent.thirteenth.p5")))}
          </p>
          <p>
            {renderHTML(String(t("policyPage.tableContent.thirteenth.p6")))}
          </p>
        </div>

        <div
          id={"review-data"}
          className="gap-2 flex flex-col text-14px md:text-[16px] leading-[25px] md:leading-[30px]"
        >
          <h2 className="text-[20px] md:text-[24px] font-600 uppercase my-[20px]">
            14. {String(t("policyPage.table.t14"))}
          </h2>

          <p>
            {renderHTML(String(t("policyPage.tableContent.fourteenth.p1")))}
          </p>
        </div>
      </div>
    </div>
  );
};
